import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {TRANSLATE, TranslatePipe} from '@brunata-angular/util';
import {outputFormatLabel} from '../../models/output-format.model';
import {FileFormatBadgeComponent} from '../file-format-badge/file-format-badge.component';
import {OptionItem} from './option-card.model';

@Component({
    selector: 'app-option-card',
    standalone: true,
    imports: [TranslatePipe, FileFormatBadgeComponent],
    template: `
        <label
            class="option-card d-flex user-select-none mb-0 flex-fill"
            [class.selected]="selected()"
            style="cursor: pointer">
            <span class="option-row" [class.format-row]="item().outputFormat !== undefined">
                <input
                    type="radio"
                    class="form-check-input"
                    [name]="name()"
                    [checked]="selected()"
                    (change)="optionSelected.emit()" />
                @if (item().outputFormat; as outputFormat) {
                    <app-file-format-badge [format]="outputFormat" />
                    @if (badgeQualifier(); as qualifier) {
                        <span class="fw-bold">({{ qualifier }})</span>
                    }
                } @else {
                    <span class="fw-bold">
                        {{ item().titleKey | translate: 'online' : 'export' : item().titleParams }}
                    </span>
                }
            </span>
            <span class="option-description text-muted small">
                {{
                    item().descriptionKey
                        | translate: 'online' : 'export' : item().descriptionParams
                }}
            </span>
        </label>
    `,
    styles: `
        .option-card {
            --bs-border-color: rgba(var(--bs-primary-rgb), 0.3);
            flex-direction: column;
            gap: 0.25rem;
            padding: 0.75rem 1rem;
        }

        .option-card.selected {
            background-color: var(--brunata-blue-faint);
        }

        .option-card:has(:focus-visible) {
            outline: 2px solid var(--bs-primary);
            outline-offset: -2px;
        }

        .option-row {
            display: flex;
            align-items: flex-start;
            gap: 0.625rem;
        }

        .option-row .form-check-input {
            flex: none;
            width: 1.125rem;
            height: 1.125rem;
            margin: 0.1875rem 0 0;
            cursor: pointer;
        }

        .option-row .form-check-input:focus {
            box-shadow: none;
        }

        .option-row.format-row {
            align-items: center;
        }

        .option-row.format-row .form-check-input {
            margin-top: 0;
        }

        .option-description {
            padding-left: 1.75rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionCardComponent {
    readonly item = input.required<OptionItem>();
    readonly selected = input.required<boolean>();
    readonly name = input.required<string>();
    readonly optionSelected = output<void>();

    private readonly translate = inject(TRANSLATE);

    protected readonly badgeQualifier = computed(() => {
        const {outputFormat, titleKey} = this.item();
        if (outputFormat === undefined) {
            return null;
        }
        const title = this.translate(titleKey, 'online', 'export');
        return title === outputFormatLabel(outputFormat) ? null : title;
    });
}
